import { Button, Modal, Space, message } from 'antd';
import { finishOrder, startOrder } from 'api/coreapi/orders';
import { InternalOrderActionsUpdatedEvent } from 'constants/events';
import { OrderManagementModes, OrderStatuses } from 'constants/orders';
import { PermissionList } from 'constants/permissions';
import usePermission from 'features/auth/usePermission';
import { useState } from 'react';
import { Order } from 'shared/models/orderModel';

interface OrderExecutionActionsProps {
    order: Order;
    onTransitioned: () => Promise<void> | void;
}

type Transition = 'start' | 'finish';

const OrderExecutionActions = ({ order, onTransitioned }: OrderExecutionActionsProps) => {
    const canManageInternalOrders = usePermission([PermissionList.OrderManageInternal]);
    const [transition, setTransition] = useState<Transition>();
    const [submitting, setSubmitting] = useState(false);

    if (!canManageInternalOrders
        || order.managementModeId !== OrderManagementModes.InternallyManaged) {
        return null;
    }

    const availableTransition = order.orderStatusId === OrderStatuses.Scheduled
        ? 'start'
        : order.orderStatusId === OrderStatuses.InProgress
            ? 'finish'
            : undefined;

    if (!availableTransition) {
        return null;
    }

    const isStarting = availableTransition === 'start';
    const actionLabel = isStarting ? 'Iniciar servicio' : 'Finalizar servicio';

    const submit = async () => {
        if (!transition) {
            return;
        }

        try {
            setSubmitting(true);
            const response = transition === 'start'
                ? await startOrder(order.orderNumber)
                : await finishOrder(order.orderNumber);

            if (!response.result) {
                throw new Error('La API no confirmó la transición de la orden.');
            }

            message.success(transition === 'start'
                ? 'El servicio fue iniciado correctamente.'
                : 'El servicio fue finalizado correctamente.');
            setTransition(undefined);
            window.dispatchEvent(new Event(InternalOrderActionsUpdatedEvent));
            await onTransitioned();
        } catch (error: any) {
            if (error?.statusCode === 409) {
                message.warning('El estado de la orden cambió o la acción ya fue realizada. Se recargó la información.');
                setTransition(undefined);
                window.dispatchEvent(new Event(InternalOrderActionsUpdatedEvent));
                await onTransitioned();
                return;
            }

            message.error(error?.errors?.[0] ?? `No fue posible ${isStarting ? 'iniciar' : 'finalizar'} el servicio.`);
        } finally {
            setSubmitting(false);
        }
    };

    return <Space>
        <Button
            type="primary"
            loading={submitting}
            disabled={submitting}
            onClick={() => setTransition(availableTransition)}>
            {actionLabel}
        </Button>
        <Modal
            title={`${actionLabel} para la orden #${order.orderNumber}`}
            open={transition !== undefined}
            okText={actionLabel}
            cancelText="Cancelar"
            confirmLoading={submitting}
            okButtonProps={{ disabled: submitting }}
            cancelButtonProps={{ disabled: submitting }}
            onOk={submit}
            onCancel={() => setTransition(undefined)}>
            <p>¿Confirmas que deseas {isStarting ? 'iniciar' : 'finalizar'} este servicio?</p>
        </Modal>
    </Space>;
};

export default OrderExecutionActions;
